import type React from 'react';
import { useState, useEffect } from 'react';
import {
  UiModal,
  UiModalOverlay,
  UiModalContent,
  UiModalHeader,
  UiModalCloseButton,
  UiModalBody,
  UiModalFooter,
  UiButton,
  UiFormControl,
  UiFormLabel,
  UiInput,
  UiRadioGroup,
  UiRadio,
  UiCheckbox,
  UiStack,
  UiHStack,
  UiText,
  UiFormErrorMessage,
  uiStyles
} from '@/lib/ui';
import BaseMessageBarInfo from '@/base/MessageBar/Info';
import BaseDividerHorizontal from '@/base/Divider/Horizontal';
import { type TableView } from '@/api/common/tableViews';

interface SaveViewModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (name: string, scope: 'personal' | 'tenant', isDefault: boolean) => Promise<void>
  mode: 'saveAs' | 'new'
  currentView: TableView | null
  existingViewNames: string[]
}

export const SaveViewModal: React.FC<SaveViewModalProps> = ({
  isOpen,
  onClose,
  onSave,
  mode,
  currentView,
  existingViewNames
}) => {
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'personal' | 'tenant'>('personal');
  const [isDefault, setIsDefault] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (mode === 'saveAs' && currentView) {
        setName(`Copy of ${currentView.name}`);
        setScope('personal');
        setIsDefault(false);
      } else {
        setName('');
        setScope('personal');
        setIsDefault(false);
      }
      setError('');
    }
  }, [isOpen, mode, currentView]);

  const getModalTitle = () => {
    switch (mode) {
      case 'saveAs':
        return 'Save View As';
      case 'new':
        return 'Create New View';
      default:
        return 'Create New View';
    }
  };

  const validateForm = () => {
    if (!name.trim()) {
      setError('View name is required');
      return false;
    }

    // Check for duplicate names
    if (existingViewNames.includes(name)) {
      setError('A view with this name already exists');
      return false;
    }

    if (name.length > 100) {
      setError('View name must be less than 100 characters');
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setError('');

    try {
      await onSave(name, scope, isDefault);
      onClose();
    } catch (err) {
      setError('Failed to save view. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <UiModal isOpen={isOpen} onClose={handleClose} size="md">
      <UiModalOverlay />
      <UiModalContent>
        <UiModalHeader>{getModalTitle()}</UiModalHeader>
        <UiModalCloseButton isDisabled={isLoading} />

        <UiModalBody>
          <UiStack spacing={4}>
            {/* View Name */}
            <UiFormControl isInvalid={!!error}>
              <UiFormLabel>
                <UiHStack spacing={1}>
                  <UiText>Name</UiText>
                  <UiText color="red.500">*</UiText>
                </UiHStack>
              </UiFormLabel>
              <UiInput
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setError('');
                }}
                placeholder="Enter a descriptive name for this view"
                autoFocus
                disabled={isLoading}
              />
              {error && <UiFormErrorMessage>{error}</UiFormErrorMessage>}
            </UiFormControl>

            {/* Scope Selection */}
            <UiFormControl>
              <UiFormLabel>
                <UiHStack spacing={1}>
                  <UiText>View visibility</UiText>
                  <UiText color="red.500">*</UiText>
                </UiHStack>
              </UiFormLabel>
              <UiRadioGroup
                value={scope}
                onChange={(value) => { return setScope(value as 'personal' | 'tenant'); }}
                isDisabled={isLoading}
              >
                <UiStack spacing={3}>
                  <UiRadio value="personal" colorScheme="primary">
                    <UiStack spacing={0}>
                      <UiText>
                          Personal view
                      </UiText>
                      <UiText color="gray.500" fontSize="sm">
                          Only visible to you
                      </UiText>
                    </UiStack>
                  </UiRadio>

                  <UiRadio value="tenant" colorScheme="primary">
                    <UiStack spacing={0}>
                      <UiText>
                          Team view
                      </UiText>
                      <UiText color="gray.500" fontSize="sm">
                          Visible to all team members
                      </UiText>
                    </UiStack>
                  </UiRadio>
                </UiStack>
              </UiRadioGroup>
            </UiFormControl>

            {/* Spacer */}
            <UiStack h={4} />

            {/* Default View Checkbox */}
            <UiFormControl>
              <UiCheckbox
                isChecked={isDefault}
                onChange={(e) => { return setIsDefault(e.target.checked); }}
                isDisabled={isLoading}
              >
                <UiText>Set as my default view</UiText>
              </UiCheckbox>
              <UiText color="gray.500" fontSize="sm" mt={1} ml={6}>
                This view will load automatically when you visit this page
              </UiText>
            </UiFormControl>

            {/* Info Alert for Team View */}
            {scope === 'tenant' && (
              <BaseMessageBarInfo>
                <UiText fontSize="sm">
                  Team views are shared with all members of your organization.
                </UiText>
              </BaseMessageBarInfo>
            )}
          </UiStack>
        </UiModalBody>

        <UiModalFooter>
          <UiHStack spacing={3} justifyContent="flex-end">
            <UiButton
              variant="ghost"
              onClick={handleClose}
              isDisabled={isLoading}
            >
              Cancel
            </UiButton>
            <UiButton
              colorScheme="primary"
              onClick={handleSave}
              isLoading={isLoading}
              loadingText="Saving..."
            >
              Create view
            </UiButton>
          </UiHStack>
        </UiModalFooter>
      </UiModalContent>
    </UiModal>
  );
};
